// geetest.js
'use strict';
import loadScriptTag from '../resource/loadScriptTag.js';
import { captcha } from '../resource/debug.js';
import { getSentry } from '../resource/sentry.js';
// import env from '../resource/env.js';

const log = captcha.extend('log:geetest');
const errorLog = captcha.extend('error:geetest');

const SCRIPT_ID = 'geetest-v4-script-id';

// const isProd = env.NODE_ENV === 'production';
let loadScriptPromise = null;

/**
 * Load GeeTest v4 SDK script only (without initializing)
 * @return {Promise<void>}
 */
export const loadGeeTestScript = async () => {
  log('loadGeeTestScript');

  // script is loading
  if (loadScriptPromise) {
    log('geetest script loading in progress, waiting...');
    return loadScriptPromise;
  }

  try {
    if (!document.getElementById(SCRIPT_ID)) {
      log('load geetest v4 script');

      loadScriptPromise = loadScriptTag({
        id: SCRIPT_ID,
        async: true,
        src: 'https://static.geetest.com/v4/gt4.js',
      });

      await loadScriptPromise;
      loadScriptPromise = null;

      if (!window.initGeetest4) {
        throw new Error(
          'GeeTest v4 script load failed: initGeetest4 not found'
        );
      }
      log('load geetest script done');
    } else {
      log('geetest script already loaded');
    }
  } catch (e) {
    loadScriptPromise = null;
    errorLog('loadGeeTestScript error', e);
    getSentry().then(({ withScope, captureException }) => {
      withScope(scope => {
        scope.setFingerprint(['captcha', 'geetest', 'script_load']);
        scope.setTag('record', 'captcha');
        scope.setTag('captcha_provider', 'geetest');
        scope.setTag('captcha_stage', 'script_load');
        scope.setExtra('error_message', e?.message);
        captureException(e);
      });
    });

    throw e;
  }
};

/**
 * Initialize GeeTest CAPTCHA
 * @param {Object} params
 * @param {string} params.captchaId - GeeTest Captcha ID
 * @param {string} [params.product='bind'] - Product type: 'popup' | 'float' | 'bind' | 'custom'
 * @param {Function} params.onSuccess - Success callback with verification result
 * @param {Function} params.onError - Error callback
 * @param {Function} params.onReady - Ready callback when widget is ready
 * @param {Function} params.onVerificationStart - Callback when user starts verification (onNextReady)
 * @param {Function} params.onFail - Callback when verification fails
 * @return {Promise<Object>} GeeTest instance
 */
export const initGeeTest = async ({
  captchaId,
  product = 'bind',
  onSuccess,
  onError,
  onReady,
  onVerificationStart,
  onFail,
}) => {
  // if (!isProd) return null;

  log('initGeeTest', { captchaId, product });

  try {
    await loadGeeTestScript();

    if (!window.initGeetest4) {
      throw new Error('initGeetest4 is not available');
    }

    return new Promise(resolve => {
      window.initGeetest4(
        {
          captchaId,
          product,
        },
        captchaInstance => {
          log('GeeTest initialized successfully');

          // Listen for ready event
          captchaInstance.onReady(() => {
            log('GeeTest ready');
            if (onReady) {
              onReady();
            }
          });

          // Listen for verification start event
          captchaInstance.onNextReady(() => {
            log('GeeTest next stage ready - user started verification');
            if (onVerificationStart) {
              onVerificationStart();
            }
          });

          // Listen for verification success event
          captchaInstance.onSuccess(() => {
            const result = captchaInstance.getValidate();
            log('GeeTest verification success', result);
            if (onSuccess) {
              onSuccess(result);
            }
          });

          // Listen for verification fail event
          captchaInstance.onFail(() => {
            log('GeeTest verification failed - user can retry');
            if (onFail) {
              onFail();
            }
          });

          // Listen for error event
          captchaInstance.onError(error => {
            errorLog('GeeTest error', error);
            getSentry().then(({ withScope, captureException }) => {
              withScope(scope => {
                scope.setFingerprint(['captcha', 'geetest', 'runtime_error']);
                scope.setTag('record', 'captcha');
                scope.setTag('captcha_provider', 'geetest');
                scope.setTag('captcha_stage', 'runtime_error');
                scope.setExtra('geetest_error', error);
                captureException(new Error('GeeTest runtime error'));
              });
            });

            if (onError) {
              onError(error);
            }
          });

          resolve(captchaInstance);
        }
      );
    });
  } catch (e) {
    errorLog('initGeeTest error', e);
    getSentry().then(({ withScope, captureException }) => {
      withScope(scope => {
        scope.setFingerprint(['captcha', 'geetest', 'init_failed']);
        scope.setTag('record', 'captcha');
        scope.setTag('captcha_provider', 'geetest');
        scope.setTag('captcha_stage', 'init_failed');
        scope.setExtra('error_message', e?.message);
        scope.setExtra('captchaId', captchaId);
        captureException(e);
      });
    });

    if (onError) onError(e);
    throw e;
  }
};
